// components/ImageGallerySection.tsx
"use client";

import React, { useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { ImageGallerySectionData } from "@/app/utilities/interfaces";
import Heading from "../Heading";
import SectionDescription from "@/component/ui/SectionDescription/SectionDescription";
import {
    CarouselViewAllFooter,
    CarouselViewAllHeader,
} from "../CarouselViewAll";

const CarImageCardModal = dynamic(() => import("./CarImageCardModal"));

// Gallery is only meaningful with a decent number of images.
const MIN_IMAGES_TO_SHOW = 5;

interface ImageGallerySectionProps {
    data: ImageGallerySectionData;
    isMobile?: boolean;
    /** When set, opens the image-page lightbox via parent instead of CarImageCardModal. */
    onImageSelect?: (index: number) => void;
}

const ImageGallerySection: React.FC<ImageGallerySectionProps> = ({ data, isMobile = false, onImageSelect }) => {
    const { title, description, viewAllLabel, viewAllHref, viewAllTitle, images } = data;

    // Index of the image the popup should open on (null = closed).
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    const openImage = (index: number) => {
        if (onImageSelect) {
            onImageSelect(index);
            return;
        }
        setSelectedIndex(index);
    };

    // Stable identity so the popup doesn't reset its slide on every render.
    const modalData = {
        title,
        images: images?.map((img) => img.src) ?? [],
        imageCount: images?.length ?? 0,
    };

    // Visibility rule: only render when we have enough images.
    if (!images || images.length < MIN_IMAGES_TO_SHOW) return null;

    const totalCount = images.length;
    // Image used behind the "View All" tile (6th image if present, else fall back).
    const viewAllImage = images[5] ?? images[4] ?? images[0];
    const headerViewAllTitle = viewAllTitle || viewAllLabel;
    const showHeaderViewAll = Boolean(viewAllHref);

    return (
        <section className="w-full py-4">
            {/* ── Header ── */}
            <div className="mb-4 md:mb-6">
                <div className={showHeaderViewAll ? "flex items-center justify-between gap-4" : ""}>
                    <Heading tag="h2" className="min-w-0 flex-1 !text-[20px] md:!text-[23px] font-bold text-gray-900">
                        {title}
                    </Heading>
                    {showHeaderViewAll && (
                        <CarouselViewAllHeader
                            href={viewAllHref!}
                            title={headerViewAllTitle}
                            label={viewAllLabel}
                        />
                    )}
                </div>
                <SectionDescription text={description} className="mt-2" />
            </div>

            {/* ══════════════════════════════════════
          DESKTOP GRID (3 columns)
      ══════════════════════════════════════ */}
            <div
                className="hidden md:grid gap-3 rounded-[24px] overflow-hidden border border-gray-200"
                style={{
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gridTemplateRows: "180px 180px 180px",
                    gridTemplateAreas: `
            "hero hero rt"
            "hero hero rb"
            "bl   bc   va"
          `,
                }}
            >
                {/* Hero Image - Spans 2 cols, 2 rows */}
                {images[0]?.src && (
                    <button type="button" aria-label={images[0].alt} onClick={() => openImage(0)} className="relative overflow-hidden bg-gray-100 cursor-pointer focus:outline-none" style={{ gridArea: "hero" }}>
                        <Image src={images[0].src} alt={images[0].alt} title={images[0].alt} fill className="object-cover" sizes="(max-width: 1200px) 66vw, 662px" priority={!isMobile} />
                    </button>
                )}

                {/* Top Right Stack */}
                {images[1]?.src && (
                    <button type="button" aria-label={images[1].alt} onClick={() => openImage(1)} className="relative overflow-hidden bg-gray-100 cursor-pointer focus:outline-none" style={{ gridArea: "rt" }}>
                        <Image src={images[1].src} alt={images[1].alt} title={images[1].alt} fill className="object-cover" sizes="33vw" />
                    </button>
                )}

                {/* Middle Right Stack */}
                {images[2]?.src && (
                    <button type="button" aria-label={images[2].alt} onClick={() => openImage(2)} className="relative overflow-hidden bg-gray-100 cursor-pointer focus:outline-none" style={{ gridArea: "rb" }}>
                        <Image src={images[2].src} alt={images[2].alt} title={images[2].alt} fill className="object-cover" sizes="33vw" />
                    </button>
                )}

                {/* Bottom Left */}
                {images[3]?.src && (
                    <button type="button" aria-label={images[3].alt} onClick={() => openImage(3)} className="relative overflow-hidden bg-gray-100 cursor-pointer focus:outline-none" style={{ gridArea: "bl" }}>
                        <Image src={images[3].src} alt={images[3].alt} title={images[3].alt} fill className="object-cover" sizes="33vw" />
                    </button>
                )}

                {/* Bottom Center */}
                {images[4]?.src && (
                    <button type="button" aria-label={images[4].alt} onClick={() => openImage(4)} className="relative overflow-hidden bg-gray-100 cursor-pointer focus:outline-none" style={{ gridArea: "bc" }}>
                        <Image src={images[4].src} alt={images[4].alt} title={images[4].alt} fill className="object-cover" sizes="33vw" />
                    </button>
                )}

                {/* View All Tile */}
                {viewAllImage?.src && (
                    <button type="button" aria-label={viewAllLabel ?? "View all images"} onClick={() => openImage(0)} className="relative overflow-hidden bg-gray-900 cursor-pointer focus:outline-none" style={{ gridArea: "va" }}>
                        <Image src={viewAllImage.src} alt="View all images" title="View all images" fill className="object-cover opacity-40" sizes="33vw" />
                        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-white">
                            <span className="font-bold text-lg flex items-center gap-2">
                                {viewAllLabel ?? "View All"}
                                <Image src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`} alt="View all arrow" title="View all arrow" width={20} height={20} />
                            </span>
                            <span className="text-xs opacity-90">{totalCount} Images</span>
                        </span>
                    </button>
                )}
            </div>

            {/* ══════════════════════════════════════
          MOBILE LAYOUT
      ══════════════════════════════════════ */}
            <div className="flex flex-col gap-3 md:hidden">
                {/* Main Hero */}
                {images[0]?.src && (
                    <button type="button" aria-label={images[0].alt} onClick={() => openImage(0)} className="relative w-full h-[220px] overflow-hidden rounded-2xl bg-gray-100 cursor-pointer focus:outline-none">
                        <Image src={images[0].src} alt={images[0].alt} title={images[0].alt} fill className="object-cover" sizes="100vw" priority={isMobile} />
                    </button>
                )}

                {/* Thumbnail Row */}
                <div className="grid grid-cols-4 gap-2">
                    {images.slice(1, 4).map((img, i) => (
                        <button type="button" key={img.id} aria-label={img.alt} onClick={() => openImage(i + 1)} className="relative h-[64px] overflow-hidden rounded-xl bg-gray-100 cursor-pointer focus:outline-none">
                            <Image src={img.src} alt={img.alt} title={img.alt} fill className="object-contain" sizes="25vw" />
                        </button>
                    ))}

                    {/* Mobile View All */}
                    {images[4]?.src && (
                        <button type="button" aria-label={viewAllLabel || "View all images"} onClick={() => openImage(0)} className="relative h-[64px] overflow-hidden rounded-xl bg-black cursor-pointer focus:outline-none">
                            <Image src={images[4].src} alt="View all images" title="View all images" fill className="object-contain opacity-40" sizes="25vw" />
                            <span className="absolute inset-0 flex items-center justify-center p-1 text-center">
                                <span className="text-[10px] font-bold text-white leading-tight">
                                    {viewAllLabel || "View All"}
                                    <br />
                                    ({totalCount})
                                </span>
                            </span>
                        </button>
                    )}
                </div>
            </div>

            {showHeaderViewAll && (
                <CarouselViewAllFooter
                    href={viewAllHref!}
                    title={headerViewAllTitle}
                    label={viewAllLabel}
                    wrapperClassName="mt-4 md:mt-0"
                />
            )}

            {!onImageSelect && (
                <CarImageCardModal
                    open={selectedIndex !== null}
                    data={modalData}
                    startIndex={selectedIndex ?? 0}
                    onClose={() => setSelectedIndex(null)}
                />
            )}
        </section>
    );
};

export default ImageGallerySection;
